/**
 * BanUserDialog.tsx — 封禁用户弹窗（注册名 ban-user）
 *
 * 1:1 移植自原型 design/Novel-admin/src/dialogs.jsx 1250–1310，已接真（ADM-USR-S003）：
 * 「确认封禁」走 POST /api/v1/users/:id/ban（usersApi.ban + useMutation），body { reason }
 * 为纯 string 契约（BanUserRequestSchema —— 封禁原因记操作日志，非 C 端展示，不走 i18n）；
 * reason = 原因 Select + 可选备注拼接。成功按服务端真值 toast + invalidate ['users'] 刷新列表，
 * 失败错误 toast 不关弹窗（不假成功）。用户卡展示传入 user 真值（原型为硬编码示例）。
 * 「同时执行」三个联动勾选契约无对应字段 → disabled + NOT-in-scope 标注（不静默丢弃输入）。
 */

import { onlineManager, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { UserRow } from '~/data/queries';
import { usersApi } from '~/hooks/use-users';
import { fmtCountry } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { Avatar } from '~/ui/Avatar';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UiFormRow } from '~/ui/FormRow';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';

// design dialogs.jsx 的薄包装：固定 dialogs 几何，与原型 1:1
const FormRow = (p: FormRowProps) => (
  <UiFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

const BAN_REASONS = [
  '风控自动触发',
  '违反社区规范',
  '恶意刷量 / 套利',
  '账号被盗 / 异常登录',
  '用户主动申请',
  '其他',
];

const NOT_IN_SCOPE_TITLE =
  '待契约（NOT-in-scope，见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）';

export function BanUserDialog({
  onClose,
  user,
  onBanned,
}: { onClose: () => void; user?: UserRow; onBanned?: () => void }) {
  // 默认中性「其他」而非 BAN_REASONS[0]='风控自动触发'：admin 在弹窗发起的是**人工封禁**，
  // 默认到风控原因会把人工封禁误记为自动风控封禁、污染 audit reason（codex #54 bot）。
  // 「其他」是非风控中性默认，运营可改选具体原因。
  const [reason, setReason] = useState('其他');
  const [note, setNote] = useState('');
  const queryClient = useQueryClient();
  const banMutation = useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: (u: UserRow) => {
      if (!onlineManager.isOnline()) throw new Error('当前离线，请联网后重新确认操作');
      // 契约 reason 纯 string（≤500）：原因 Select + 可选备注拼接，超长裁剪兜底
      const composed = note.trim() ? `${reason} · ${note.trim()}` : reason;
      return usersApi.ban(u.id, composed.slice(0, 500));
    },
    onSuccess: (dto) => {
      // toast 用服务端返回真值（封禁后的用户详情 DTO）
      toast(`已封禁 ${dto.display_name ?? dto.email ?? dto.id}`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // 同时刷新顶部 KPI 封禁/风控计数（user-stats 独立缓存，否则封禁后卡片计数 stale，codex #115 三轮）
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
      // 从用户详情抽屉发起时，抽屉绑定的是封禁前 user 快照——关掉抽屉避免显示陈旧状态/操作
      // （codex #54 bot：status 写后已开抽屉不刷新）。从行菜单发起时 onBanned 关 null 抽屉无害。
      onBanned?.();
      onClose();
    },
    onError: (err) => {
      toast(`${user?.name ?? '封禁'} · ${err instanceof Error ? err.message : '操作失败'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  return (
    <Dialog
      width={520}
      title="封禁账户"
      subtitle="封禁后该账户将无法登录 · 已购权益保留"
      icon="IconBan"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} style={{ marginLeft: 'auto' }}>
            取消
          </button>
          <button
            className="btn btn-accent"
            style={{ background: 'var(--neg)', borderColor: 'var(--neg)' }}
            disabled={!user || banMutation.isPending}
            onClick={() => {
              if (!onlineManager.isOnline()) {
                toast('当前离线，请联网后重新确认操作', { tone: 'neg' });
                return;
              }
              if (user) banMutation.mutate(user);
            }}
          >
            <Icons.IconBan size={11} />
            确认封禁
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <div
          className="card-flat"
          style={{
            padding: 12,
            background: 'var(--neg-soft)',
            borderColor: 'transparent',
            marginBottom: 14,
          }}
        >
          <div className="hstack" style={{ gap: 10 }}>
            <Avatar seed={user?.id ?? 'unknown'} size={32} fontSize={13}>
              {user?.name?.[0] ?? '?'}
            </Avatar>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontSize: 12.5 }}>{user?.name ?? '—'}</div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                {user?.id ?? '—'}
                {user?.appName ? ` · ${user.appName}` : ''}
                {user?.country ? ` (${fmtCountry(user.country)})` : ''} · 累计{' '}
                {/* 累计充值 per-currency（catch-up #130）：adapter 已含币种符号 / '—' */}
                {user?.totalSpend ?? '—'}
              </div>
            </div>
            {user && (
              <span
                className={`chip ${user.status === '正常' ? 'chip-pos' : user.status === '已封禁' ? 'chip-neg' : 'chip-warn'}`}
                style={{ fontSize: 10 }}
              >
                ● {user.status}
              </span>
            )}
          </div>
        </div>
        <FormRow label="封禁原因" required hint="将记录到操作日志">
          <Select className="input" value={reason} onChange={(e) => setReason(e.target.value)}>
            {BAN_REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="备注">
          <textarea
            className="input"
            rows={3}
            placeholder="补充说明…"
            maxLength={400}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ height: 'auto' }}
          />
        </FormRow>
        <FormRow label="同时执行">
          {/* 契约无「清余额 / 封设备 / 取消订阅」联动字段 → 保留视觉但禁用，避免勾了被静默丢弃 */}
          <div className="vstack" style={{ gap: 6 }}>
            {[
              '清空账户 COIN / BONUS 余额',
              '同步封禁该用户最近 3 个设备',
              '取消所有进行中订阅',
            ].map((label) => (
              <label
                key={label}
                className="hstack"
                title={NOT_IN_SCOPE_TITLE}
                style={{
                  gap: 6,
                  fontSize: 12,
                  cursor: 'not-allowed',
                  color: 'var(--fg-muted)',
                  opacity: 0.6,
                }}
              >
                <input type="checkbox" disabled />
                {label}
              </label>
            ))}
          </div>
        </FormRow>
      </div>
    </Dialog>
  );
}
